import Head from 'next/head'
import Link from 'next/link'
import BrandWordmark from '../components/BrandWordmark'

export default function TermsPage() {
  return (
    <>
    <Head>
      <title>Terms &amp; Conditions — SkillBets</title>
      <meta name="description" content="Read the SkillBets Terms and Conditions governing use of our sports prediction analytics and virtual betting platform." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SkillBets" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:title" content="Terms and Conditions — SkillBets" />
      <meta property="og:description" content="Read the SkillBets Terms and Conditions governing use of our sports prediction analytics and virtual betting platform." />
      <link rel="canonical" href="https://skillbets.co.za/terms/" />
      <meta property="og:url" content="https://skillbets.co.za/terms" />
      <meta property="og:image" content="https://skillbets.co.za/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SkillBetsZA" />
      <meta name="twitter:title" content="Terms and Conditions — SkillBets" />
      <meta name="twitter:description" content="Read the SkillBets Terms and Conditions governing use of our sports prediction analytics and virtual betting platform." />
      <meta name="twitter:image" content="https://skillbets.co.za/og-image.png" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@800&display=swap" rel="stylesheet" />
    </Head>
    <main className="min-h-screen bg-gray-900 text-white">
      {/* Compliance ribbon */}
      <div className="w-full bg-black/40 px-4 py-2 text-center text-[11px] text-white/70">
        Simulation & analytics only • No cash wagering or payouts • SkillBets is not a bookmaker
      </div>

      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link href="/" className="flex items-center" aria-label="SkillBets home">
            <BrandWordmark />
          </Link>
          <Link
            href="/"
            className="rounded-xl bg-[#3EA8F5] px-3 py-2 text-xs font-bold text-gray-900 hover:brightness-110"
            data-testid="button-back-home"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Body */}
      <article className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold">Terms & Conditions</h1>
        <p className="mt-3 max-w-3xl text-white/70">
          These Terms govern your access to and use of the SkillBets website, mobile site, and related services (the
          "Service"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not
          use the Service.
        </p>
        <p className="mt-2 text-xs text-white/50" suppressHydrationWarning>Last updated: 2025-01-01</p>

        {/* Overview / quick facts */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
            <div className="font-semibold text-white">Nature of Service</div>
            SkillBets provides <strong>simulation and analytics tools</strong> for sports predictions using <strong>virtual currency</strong> only.
            We <strong>do not</strong> accept real‑money bets, <strong>do not</strong> pay cash winnings, and are <strong>not</strong> a bookmaker.
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
            <div className="font-semibold text-white">Age & Eligibility</div>
            The Service is for <strong>adults 18+</strong> only. By using the Service you confirm you are 18+ and legally capable of
            entering binding agreements in your jurisdiction.
          </div>
        </div>

        <section className="prose prose-invert mt-10 max-w-none">
          <h2 className="text-2xl font-bold mt-8 mb-4">1. Definitions</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li><strong>"Virtual Currency"</strong> means non‑monetary units used inside the Service solely for simulation. Virtual Currency has no cash value and cannot be redeemed, transferred, or sold.</li>
            <li><strong>"You/Your"</strong> means the person creating an account or using the Service.</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">2. Nature of Service (No Gambling)</h2>
          <p className="text-white/80">
            The Service is a <strong>practice environment</strong> with analytics. It uses <strong>Virtual Currency</strong> and may display leaderboards for engagement.
            <strong> No real‑money wagering</strong> occurs on SkillBets, <strong>no cash payouts</strong> are offered, and Virtual Currency <strong>cannot be exchanged for cash, prizes, or goods</strong>.
            We are not a bookmaker and do not facilitate betting.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">3. Eligibility & Accounts</h2>
          <ol className="list-decimal pl-5 text-white/80 space-y-2">
            <li>You must be <strong>18 years or older</strong> to use the Service.</li>
            <li>You agree to provide accurate information during registration (e.g., username, name, surname, phone, email, date of birth).</li>
            <li>You are responsible for safeguarding your password and for all activity under your account.</li>
            <li>We may suspend or terminate accounts for suspected misuse, multiple account abuse, fraudulent activity, or breach of these Terms.</li>
          </ol>

          <h2 className="text-2xl font-bold mt-8 mb-4">4. Virtual Currency & Simulation Rules</h2>
          <ol className="list-decimal pl-5 text-white/80 space-y-2">
            <li><strong>No monetary value.</strong> Virtual Currency is a simulated, non‑redeemable unit. It is not property, cannot be withdrawn, exchanged, gifted, or sold, and may be adjusted or revoked to protect the integrity of the Service.</li>
            <li><strong>Subscriptions.</strong> Access to the Service is provided on a subscription basis (e.g., Basic, Premium). Each plan may include a periodic <strong>Virtual Currency allowance</strong> or usage limits as described on the Pricing page. Allowances may <strong>reset each billing period</strong> and do not carry over unless expressly stated.</li>
            <li><strong>Simulation only.</strong> You may allocate Virtual Currency to simulated picks. Picks <strong>lock at kickoff</strong> and are settled after official results are confirmed, updating your Virtual Currency balance accordingly.</li>
            <li><strong>Integrity.</strong> Multi‑accounting, automation, data manipulation, or any attempt to game the simulation or leaderboards is prohibited.</li>
          </ol>

          <h2 className="text-2xl font-bold mt-8 mb-4">5. Plans, Billing & Cancellation</h2>
          <ol className="list-decimal pl-5 text-white/80 space-y-2">
            <li><strong>Pricing.</strong> Plans are subscription‑based and priced in ZAR (South African Rand). Current plan names and features are shown on the Pricing page and may change with notice.</li>
            <li><strong>Renewals.</strong> Paid plans renew monthly until cancelled. You authorise us or our payment processor to charge the applicable fees and taxes.</li>
            <li><strong>Cancellation.</strong> You can cancel anytime from your account settings; access continues until the end of the paid period.</li>
            <li><strong>Refunds.</strong> Unless required by law, fees are non‑refundable once a billing period begins. This does not affect your statutory rights.</li>
            <li><strong>Trials & Promo Codes.</strong> We may offer trials or promotions subject to additional terms. Misuse may void the benefit and/or lead to account action.</li>
          </ol>

          <h2 className="text-2xl font-bold mt-8 mb-4">6. Acceptable Use</h2>
          <ol className="list-decimal pl-5 text-white/80 space-y-2">
            <li>No unlawful, harmful, defamatory, or abusive content or conduct.</li>
            <li>No scraping, reverse engineering, or bulk data extraction without written consent.</li>
            <li>No cheating or manipulation of simulations, results, or leaderboards (e.g., multi‑accounting, automation, exploiting bugs).</li>
            <li>No impersonation or misrepresentation of affiliation.</li>
          </ol>

          <h2 className="text-2xl font-bold mt-8 mb-4">7. Analytics & Information Disclaimer</h2>
          <p className="text-white/80">
            The analytics, statistics, projections, and other information provided are for <strong>informational and educational
            purposes</strong> only. <strong>No outcome is guaranteed.</strong> If you choose to place real bets with a licensed operator, you do so
            at your own risk. Nothing on the Service is financial, legal, or betting advice.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">8. Privacy & POPIA</h2>
          <p className="text-white/80">
            We process your personal information in line with the <strong>Protection of Personal Information Act (POPIA)</strong> and our
            <a className="text-[#3EA8F5] underline ml-1" href="/privacy">Privacy Policy</a>. We collect the minimum data required for
            account creation, security, and service operation, and we implement reasonable safeguards against unlawful
            access, loss, or misuse.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">9. Intellectual Property</h2>
          <p className="text-white/80">
            The Service and its content (including software, design, logos, text, and analytics) are owned by or licensed
            to SkillBets and protected by applicable IP laws. You receive a limited, non‑exclusive, non‑transferable
            licence to access and use the Service for personal, non‑commercial purposes.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">10. Third‑Party Links & Services</h2>
          <p className="text-white/80">
            The Service may reference third‑party sites or data sources. We do not control and are not responsible for
            their content, accuracy, or practices. Your use of third‑party services is at your own risk and subject to
            their terms.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">11. Availability & Changes</h2>
          <p className="text-white/80">
            We may update, suspend, or discontinue all or part of the Service at any time, including features, plans, and
            pricing. We aim to provide reasonable notice for material changes where practicable.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">12. Termination</h2>
          <p className="text-white/80">
            We may suspend or terminate your access for breach of these Terms or for conduct that we reasonably believe is
            harmful to the Service or other users. You may stop using the Service at any time. Sections that by their
            nature should survive termination will survive (e.g., IP, disclaimers, limitation of liability, governing law).
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">13. Limitation of Liability</h2>
          <p className="text-white/80">
            To the maximum extent permitted by law, SkillBets and its affiliates will not be liable for any indirect, incidental, special,
            consequential, or punitive damages, or any loss of profits, data, or goodwill, arising out of or related to your use of the Service.
            Our aggregate liability for all claims will not exceed the amount you paid to SkillBets in the 12 months preceding the event giving rise to the claim.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">14. Indemnity</h2>
          <p className="text-white/80">
            You agree to indemnify and hold harmless SkillBets, its affiliates, and personnel from and against any claims,
            liabilities, damages, losses, and expenses (including reasonable legal fees) arising from your breach of these
            Terms or misuse of the Service.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">15. Governing Law & Jurisdiction</h2>
          <p className="text-white/80">
            These Terms are governed by the laws of the <strong>Republic of South Africa</strong>. You agree to the exclusive
            jurisdiction of the South African courts for any dispute arising out of or relating to the Service or these
            Terms.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">16. Changes to Terms</h2>
          <p className="text-white/80">
            We may update these Terms from time to time. If changes are material, we will provide reasonable notice (e.g.,
            by email or a notice on the Service). Your continued use of the Service after changes take effect constitutes
            acceptance of the revised Terms.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">17. Contact</h2>
          <p className="text-white/80">
            Questions? Contact <a className="text-[#3EA8F5] underline" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
          </p>
        </section>
      </article>
    </main>
    </>
  );
}
